<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getVacationBalances,
  VacationBalance,
  deleteVacationBalance
} from '@/app/core/services/vacation-balance'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableVacationBalance = ref(null)
const vacationBalanceForDelete = ref()
const urlFile = ref(null)
const refresh = ref()
const payload = ref()

/* Datos componente table */
const columns = ref([
  {
    name: 'employee_name',
    title: t('vacationBalances.list.employee'),
    table: 'employees.name'
  },
  {
    name: 'year_caused',
    title: t('vacationBalances.list.yearCaused'),
    table: 'year_caused'
  },
  {
    name: 'normal_pending_days',
    title: t('vacationBalances.list.normalPendingDays'),
    table: 'normal_pending_days'
  },
  {
    name: 'normal_total_days',
    title: t('vacationBalances.list.normalTotalDays'),
    table: 'normal_total_days'
  },
  {
    name: 'bonus_pending_days',
    title: t('vacationBalances.list.bonusPendingDays'),
    table: 'bonus_pending_days'
  },
  {
    name: 'bonus_total_days',
    title: t('vacationBalances.list.bonusTotalDays'),
    table: 'bonus_total_days'
  },
  {
    name: 'liquidation_pending_days',
    title: t('vacationBalances.list.liquidationPendingDays'),
    table: 'liquidation_pending_days'
  },
  {
    name: 'liquidation_total_days',
    title: t('vacationBalances.list.liquidationTotalDays'),
    table: 'liquidation_total_days'
  },
  {
    name: 'start_period_date',
    title: t('vacationBalances.list.startPeriodDate'),
    table: 'start_period_date'
  },
  {
    name: 'close_period_date',
    title: t('vacationBalances.list.closePeriodDate'),
    table: 'close_period_date'
  },
  {
    name: 'closed',
    title: t('vacationBalances.list.closed'),
    table: 'is_closed'
  },
  {
    name: 'updated_at',
    title: t('vacationBalances.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<VacationBalance[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createVacationBalance = () => {
  void router.push({ name: 'CreateVacationBalance' })
}
const updateVacationBalance = (id: any) => {
  void router.push({ name: 'UpdateVacationBalance', params: { id } })
}
const getVacationBalance = (id: any) => {
  void router.push({ name: 'GetVacationBalance', params: { id } })
}

/* Eliminar saldo vacaciones */
const removeVacationBalance = (values: any) => {
  titleModal.value = t('vacationBalances.list.actionStatus', {
    year: values.year_caused as string,
    employee: values.employee_name as string
  })
  descriptionModal.value = t('modal.descriptionVacationBalances')
  typeAlertModal.value = 'question'
  showModal.value = true
  vacationBalanceForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await deleteVacationBalance(vacationBalanceForDelete.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('vacationBalances.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void vacationBalances()
    })
    .catch((error: any) => {
      notificationError(error, 'vacationBalances', t, notifications, loading)
    })
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) => !field.includes('vacation_balances.id')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('vacationBalances.list.title', 1)
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'vacationBalances', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  urlFile.value = null
}

const formatDecimals = (num: number) => {
  const numStr = num.toString();
  const decimalIndex = numStr.indexOf('.');
  if (decimalIndex !== -1 && numStr.length - decimalIndex - 1 > 2) {
    return parseFloat(numStr.substring(0, decimalIndex + 3));
  }
  return num;
};

/* Listar saldo de vacaciones */
const vacationBalances = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getVacationBalances(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )

    

    // Apply the formatDecimals function to the relevant fields
    data[0].results.forEach((item: any) => {
      item.normal_pending_days = formatDecimals(item.normal_pending_days);
      item.normal_total_days = formatDecimals(item.normal_total_days);
      item.bonus_pending_days = formatDecimals(item.bonus_pending_days);
      item.bonus_total_days = formatDecimals(item.bonus_total_days);
      item.liquidation_pending_days = formatDecimals(item.liquidation_pending_days);
      item.liquidation_total_days = formatDecimals(item.liquidation_total_days);
    });


    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'vacationBalances', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="
      t('modal.acceptQuestion', {
        action: t('table.action.delete')
      })
    "
    @closeModal="closeModal"
    @otherMethod="deleteConfirm">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('vacationBalances.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('vacationBalances.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="vacationBalances" />
          <DBtn
            @click.prevent="vacationBalances"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2">
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="createVacationBalance()"
            v-if="store.hasPermission('vacation_balance:create')">
            {{
              t('form.button.create', {
                form: t('vacationBalances.list.title', 1)
              })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableVacationBalance"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), vacationBalances()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), vacationBalances()">
      <template #closed="data: any">
        <div>
          {{ data.is_closed === false ? 'No' : 'Si' }}
        </div>
      </template>
      <template #employee_inactive="data: any">
        <div>
          {{ data.employee_is_inactive === false ? 'No' : 'Si' }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('vacation_balance:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getVacationBalance(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('vacation_balance:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateVacationBalance(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('vacation_balance:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeVacationBalance(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
